import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Save, X, Trophy } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, EmptyState, Badge } from "./_ui";

const EMPTY = { title: "", description: "", starts_at: "", closes_at: "", candidates: [{ name: "", program: "" }, { name: "", program: "" }] };

export default function ElectionsPanel() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/elections").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const updateCandidate = (i, field, value) => {
    const next = [...form.candidates];
    next[i] = { ...next[i], [field]: value };
    setForm({ ...form, candidates: next });
  };
  const addCandidate = () => setForm({ ...form, candidates: [...form.candidates, { name: "", program: "" }] });
  const removeCandidate = (i) => setForm({ ...form, candidates: form.candidates.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.title || !form.starts_at || !form.closes_at) return toast.error("Titre, dates de début et clôture requis");
    const validCandidates = form.candidates.filter((c) => c.name);
    if (validCandidates.length < 2) return toast.error("Au moins 2 candidats requis");
    setBusy(true);
    try {
      await api.post("/admin/elections", {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        closes_at: new Date(form.closes_at).toISOString(),
        candidates: validCandidates,
      });
      toast.success("Élection créée");
      setAdding(false); setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette élection ?")) return;
    await api.delete(`/admin/elections/${id}`);
    toast.success("Supprimée"); await load();
  };

  if (!items) return (<><PageHeader title="Élections" /><Spinner /></>);

  return (
    <div data-testid="admin-elections">
      <PageHeader
        title="Élections"
        subtitle="Scrutins internes officiels avec candidats et plages de vote."
        action={
          !adding && (
            <PrimaryButton onClick={() => setAdding(true)} data-testid="elec-create-btn">
              <Plus size={16} /> Nouvelle élection
            </PrimaryButton>
          )
        }
      />

      {adding && (
        <Card className="p-5 mb-6">
          <Input placeholder="Intitulé de l'élection" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="elec-title" />
          <Textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-3" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Ouverture du scrutin</label>
              <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="elec-start" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Clôture</label>
              <Input type="datetime-local" value={form.closes_at} onChange={(e) => setForm({ ...form, closes_at: e.target.value })} data-testid="elec-end" />
            </div>
          </div>
          <div className="mt-5">
            <div className="text-sm font-medium text-white mb-2">Candidats</div>
            {form.candidates.map((c, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input placeholder={`Candidat ${i + 1}`} value={c.name} onChange={(e) => updateCandidate(i, "name", e.target.value)} className="flex-1" data-testid={`elec-cand-name-${i}`} />
                <Input placeholder="Programme" value={c.program} onChange={(e) => updateCandidate(i, "program", e.target.value)} className="flex-[2]" />
                <button onClick={() => removeCandidate(i)} className="px-3 text-slate-500 hover:text-[#CE1126]"><Trash2 size={14} /></button>
              </div>
            ))}
            <SecondaryButton onClick={addCandidate}><Plus size={14} /> Ajouter un candidat</SecondaryButton>
          </div>
          <div className="flex gap-3 mt-5">
            <PrimaryButton onClick={save} disabled={busy} data-testid="elec-save"><Save size={16} /> Enregistrer</PrimaryButton>
            <SecondaryButton onClick={() => { setAdding(false); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={Trophy} title="Aucune élection" hint="Programmez la prochaine consultation officielle." />
      ) : (
        <div className="space-y-4">
          {items.map((e) => {
            const open = new Date(e.closes_at) > new Date();
            return (
              <Card key={e.id} className="p-5" data-testid={`elec-${e.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Trophy size={16} className="text-[#D4AF37]" />
                      <h3 className="font-display text-lg text-white">{e.title}</h3>
                    </div>
                    {e.description && <p className="text-sm text-slate-400 mt-1">{e.description}</p>}
                    <div className="text-xs text-slate-500 mt-2">
                      Du {new Date(e.starts_at).toLocaleDateString("fr-FR")} au {new Date(e.closes_at).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge color={open ? "green" : "slate"}>{open ? "Scrutin ouvert" : "Clôturé"}</Badge>
                    <button onClick={() => remove(e.id)} className="text-slate-500 hover:text-[#CE1126]"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {e.candidates.map((c, i) => (
                    <div key={i} className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                      <div className="text-sm text-white font-medium">{c.name}</div>
                      {c.program && <div className="text-xs text-slate-400 mt-1">{c.program}</div>}
                      <div className="text-xs text-[#D4AF37] mt-2">{c.votes ?? 0} voix</div>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
